import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RotateCw } from 'lucide-react';
import {
  GlassCard,
  SettingsPageHeader,
  Pill,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { ToggleSwitch } from './ToggleSwitch';

type ToolRow = {
  toolName: string;
  label: string;
  description: string;
  locked?: boolean;
  adminDisabled?: boolean;
};

const TOOL_ROWS: ToolRow[] = [
  {
    toolName: 'Bash',
    label: 'Shell commands',
    description:
      'Anything from a directory listing to a destructive rm. Wide blast radius.',
  },
  {
    toolName: 'Read',
    label: 'Read files',
    description: 'Open files in the workspace.',
    locked: true,
  },
  {
    toolName: 'Write',
    label: 'Create / overwrite files',
    description:
      'Make new files or clobber existing ones. Affects your working tree.',
  },
  {
    toolName: 'Edit',
    label: 'Edit files',
    description:
      'Modify existing files. Same blast radius as Write — wrong path, wrong file.',
  },
  {
    toolName: 'Glob',
    label: 'Find files by pattern',
    description: 'Search the workspace tree with glob patterns.',
  },
  {
    toolName: 'Grep',
    label: 'Search file contents',
    description: 'Ripgrep across the workspace.',
  },
  {
    toolName: 'NotebookEdit',
    label: 'Edit notebooks',
    description: 'Modify Jupyter notebook cells.',
  },
  {
    toolName: 'WebFetch',
    label: 'Fetch from the web',
    description:
      'Network egress to an arbitrary URL. Lower destructive risk but a data-exfil vector.',
    adminDisabled: true,
  },
  {
    toolName: 'WebSearch',
    label: 'Web search',
    description: 'Search the public web for up-to-date information.',
  },
  {
    toolName: 'Task',
    label: 'Spawn a sub-agent',
    description:
      'Delegate a sub-task to a fresh agent with its own tool budget.',
  },
];

const DEFAULT_ASK_FIRST: string[] = [];

export function PermissionsScene() {
  const { can } = useRole();
  const isAdmin = can('roles:admin');
  const [askFirst, setAskFirst] = useState<Set<string>>(() => new Set(DEFAULT_ASK_FIRST));

  const hasCustom = useMemo(() => {
    if (askFirst.size !== DEFAULT_ASK_FIRST.length) return true;
    return DEFAULT_ASK_FIRST.some((tool) => !askFirst.has(tool));
  }, [askFirst]);

  const adminDisabledCount = TOOL_ROWS.filter((row) => row.adminDisabled).length;

  const handleToggle = (toolName: string) => (next: boolean) => {
    setAskFirst((prev) => {
      const updated = new Set(prev);
      if (next) updated.add(toolName);
      else updated.delete(toolName);
      return updated;
    });
  };

  const reset = () => setAskFirst(new Set(DEFAULT_ASK_FIRST));

  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="PermissionsScene">
        <div className="contents">
          <SurfacePageBody className="space-y-8" as="div">
            <SettingsPageHeader
              title="Permissions"
              description="Tools the assistant has to ask about before using."
            />

            <Commentable name="ToolToggles">
              <GlassCard as="section" tone="strong" className="overflow-hidden">
                <div className="border-b border-[var(--glass-border)] px-5 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-base font-medium text-zinc-900 dark:text-zinc-100">
                        Ask before running
                      </h2>
                      <p className="mt-1 text-sm text-muted">
                        When the model invokes one of these tools, the chat pauses and asks
                        you to allow or deny.
                      </p>
                    </div>
                    {hasCustom ? (
                      <button
                        type="button"
                        onClick={reset}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--glass-border-strong)] px-3 py-1.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-[var(--glass-surface-strong)] dark:text-zinc-300"
                      >
                        <RotateCw className="h-3.5 w-3.5" />
                        Reset
                      </button>
                    ) : null}
                  </div>
                </div>
                {TOOL_ROWS.map((row, idx) => {
                  const id = `tool-${row.toolName}`;
                  const descId = `${id}-desc`;
                  const checked = askFirst.has(row.toolName);
                  const locked = row.locked === true;
                  const disabled = locked || row.adminDisabled === true;

                  return (
                    <div
                      key={row.toolName}
                      className={
                        idx > 0
                          ? 'flex items-start justify-between gap-6 border-t border-[var(--glass-border)] px-5 py-4'
                          : 'flex items-start justify-between gap-6 px-5 py-4'
                      }
                    >
                      <div className="min-w-0 flex-1">
                        <label
                          htmlFor={id}
                          className={cn(
                            'block font-medium text-zinc-900 dark:text-zinc-100',
                            disabled ? 'cursor-default' : 'cursor-pointer',
                          )}
                        >
                          {row.label}
                          <span className="ml-2 text-xs font-normal uppercase tracking-wide text-zinc-400 dark:text-zinc-500">
                            {row.toolName}
                          </span>
                          {locked ? (
                            <Pill className="ml-2 normal-case tracking-normal">
                              Required by administrator
                            </Pill>
                          ) : null}
                          {row.adminDisabled ? (
                            <Pill tone="warning" className="ml-2 normal-case tracking-normal">
                              Disabled by administrator
                            </Pill>
                          ) : null}
                        </label>
                        <p id={descId} className="mt-1 text-sm text-muted">
                          {row.description}
                        </p>
                      </div>
                      <ToggleSwitch
                        id={id}
                        checked={checked}
                        disabled={disabled}
                        onCheckedChange={handleToggle(row.toolName)}
                        aria-describedby={descId}
                      />
                    </div>
                  );
                })}
              </GlassCard>
            </Commentable>

            <Commentable name="Explainer">
              <GlassCard as="section" tone="strong" className="overflow-hidden">
                <div className="px-5 py-4">
                  <h2 className="text-base font-medium text-zinc-900 dark:text-zinc-100">
                    How this interacts with permission mode
                  </h2>
                  <p className="mt-1 text-sm text-muted">
                    Per-thread permission mode (in the composer) is the coarse setting —{' '}
                    <em>Ask</em> or <em>Auto</em>. The toggles here apply in <em>Ask</em> mode:
                    the model pauses for your approval before each of these tools, unless you
                    picked &quot;Allow for thread&quot; on the in-chat card. <em>Auto</em> skips
                    every prompt — these toggles don&apos;t apply.
                  </p>
                  <p className="mt-3 text-sm text-muted">
                    Per-thread overrides (the &quot;…for thread&quot; choices on the in-chat card)
                    stay scoped to that one thread and clear when the thread is deleted.
                  </p>
                  <p className="mt-3 text-sm text-muted">
                    {isAdmin ? (
                      <>
                        Some tools are managed by your administrator via{' '}
                        <Link
                          to="/admin/builtin-tools"
                          className="text-zinc-700 underline dark:text-zinc-300"
                        >
                          Tool baseline
                        </Link>
                        .
                      </>
                    ) : (
                      <>Some tools are managed by your administrator.</>
                    )}
                  </p>
                </div>
              </GlassCard>
            </Commentable>

            {adminDisabledCount > 0 ? (
              <p className="text-sm text-muted">
                {adminDisabledCount} tool{adminDisabledCount === 1 ? '' : 's'} disabled by
                your administrator.
              </p>
            ) : null}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}
